import { useForm } from '@mantine/form';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Button,
    NativeSelect,
    Select,
    Switch,
    Text,
    TextInput,
    Alert,
} from '@mantine/core';
import {
    IconUserEdit,
    IconUserPlus,
    IconAlertCircle,
} from '@tabler/icons-react';
import { FetchResult, useMutation, useQuery } from '@apollo/client';
import { useDidUpdate } from '@mantine/hooks';

import {
    showErrorNotification,
    showLoadingNotification,
    updateFailedNotification,
    updateSuccessNotification,
} from '../../notifications';
import { numberObjectMapper } from '../../utility/numberObjectMapper';
import { generateUsername } from '../../utility/generateUsername';
import { callingCodes } from '../../constants';
import {
    Role,
    SapUser,
    Store,
    User,
    UpdateUserInput,
} from '../../codegen/graphql';
import { GET_ROLES, GET_SAP_USERS, GET_USERS } from '../../apollo/queries/user';
import { GET_STORES } from '../../apollo/queries/store';
import { CREATE_USER } from '../../apollo/mutations/user';
import { ContactNumberInput } from '../../utility/mapContactNumber';
import { useAuth } from '../../context/AuthProvider';
import { checkUserRole } from '../../utility/userRoles';
import { UserRole } from '../../types/users';
import { GET_OPS_HIERARCHY } from '../../apollo/queries/opsHierarchy';
import { mapStoreToManagerOption } from '../../helpers/storeMapping';

const storeManagerRoleId = import.meta.env.VITE_STORE_MANAGER_ID as string;

const validatePhoneNo = (contactNumber?: ContactNumberInput) => {
    if (!contactNumber?.code || !contactNumber?.number) {
        return 'Mobile Number is required';
    }
    if (!contactNumber?.code) {
        return 'Code is required';
    }
    return null;
};

export type UserFormData = Omit<UpdateUserInput, 'mobileNo' | 'telNo'> & {
    mobileNo: ContactNumberInput;
    telNo: ContactNumberInput;
};

const STORE_MANAGER_DESIGNATIONS = [
    'Store Manager',
    'Store Manager Assistant',
    'Sales Coordinator',
];

const DESIGNATIONS = [
    'Procurement Manager',
    'Divisional Manager',
    ...STORE_MANAGER_DESIGNATIONS,
    'Operations Executive',
    'Procurement Executive',
    'Operations Director',
    'Other',
];

type Props = Readonly<{
    isEditing?: boolean;
    onClose: () => void;
    updateUser: (input: UpdateUserInput) => Promise<FetchResult>;
    initialValues: UserFormData | null;
}>;

function UserForm({ isEditing, onClose, updateUser, initialValues }: Props) {
    const { authUser } = useAuth();
    const [creatingNewDesignation, setCreatingNewDesignation] = useState(false);
    const [updating, setUpdating] = useState(false);
    const [usernameWarning, setUsernameWarning] = useState<
        string | undefined
    >();

    const isAdmin = checkUserRole(authUser, [
        UserRole.SUPER_ADMIN,
        UserRole.ADMIN,
    ]);

    const { data: rolesData, error: rolesError } = useQuery(GET_ROLES);
    const { data: storesData, error: storesError } = useQuery(GET_STORES);
    const { data: sapUsersData, error: sapUsersError } =
        useQuery(GET_SAP_USERS);
    const { data: usersData } = useQuery(GET_USERS);

    const designations = useMemo(
        () => (!isAdmin ? STORE_MANAGER_DESIGNATIONS : DESIGNATIONS),
        [isAdmin],
    );

    const stores = useMemo(() => {
        if (!storesData?.getStores) {
            return [];
        }

        return storesData.getStores.map((store: Store) =>
            mapStoreToManagerOption(store, isAdmin),
        );
    }, [storesData, isAdmin]);

    const sapUsers = useMemo(() => {
        if (!sapUsersData) {
            return [];
        }

        return sapUsersData.getSapUsers.map(({ id, sapId }: SapUser) => ({
            value: id,
            label: String(sapId).toUpperCase(),
        }));
    }, [sapUsersData]);

    const roles = useMemo(() => {
        if (rolesData) {
            return rolesData.getRoles
                .filter(
                    ({ role: roleName }: Role) => roleName !== 'Super Admin',
                )
                .map(({ id, role: roleName }: Role) => ({
                    value: id,
                    label: roleName,
                }));
        }

        return [];
    }, [rolesData]);

    const initialFormValues = useMemo(() => {
        if (initialValues) {
            return {
                ...initialValues,
                designation:
                    initialValues.designation ||
                    (!isAdmin ? 'Store Manager' : ''),
                userRoleId:
                    initialValues.userRoleId ||
                    (!isAdmin ? storeManagerRoleId : ''),
                sendInvite: initialValues.sendInvite || true,
            };
        }

        return {
            firstName: '',
            lastName: '',
            username: '',
            email: '',
            designation: '',
            userRoleId: !isAdmin ? storeManagerRoleId : '',
            managerStoreId: '',
            sapUserId: '',
            mobileNo: {
                code: 'ZA/27',
                number: '',
            },
            telNo: {
                code: 'ZA/27',
                number: '',
            },
            faxNo: '',
            speedDial: '',
            sendInvite: true,
            id: '',
        };
    }, [initialValues, isAdmin, storeManagerRoleId]);

    const form = useForm<UserFormData>({
        initialValues: initialFormValues,
        validate: {
            firstName: value => (!value ? 'First Name is required' : null),
            lastName: value => (!value ? 'Last Name is required' : null),
            email: (value?: null | string) => {
                if (!value) {
                    return null;
                }
                if (!/^\S+@\S+$/.test(value)) {
                    return 'Email is invalid';
                }
                return null;
            },
            userRoleId: value => (!value ? 'Role is required' : null),
            username: (value?: null | string) =>
                !value ? 'Username is required' : null,
            designation: (value?: null | string) =>
                !value ? 'Designation is required' : null,
            mobileNo: validatePhoneNo,
            managerStoreId: value =>
                !value && !isAdmin ? 'Store is required' : null,
        },
    });

    const handleDesignationSelect = useCallback((value: string | null) => {
        if (value === 'Other') {
            form.setFieldValue('designation', '');
            setCreatingNewDesignation(true);
        } else if (value) {
            form.setFieldValue('designation', value);
            setCreatingNewDesignation(false);
        }
    }, []);

    const [createUser, { reset: resetCreateUser }] = useMutation(CREATE_USER, {
        refetchQueries: [GET_USERS, GET_OPS_HIERARCHY, GET_STORES],
        awaitRefetchQueries: true,
    });

    const renderSelectFields = useCallback(() => {
        if (!isAdmin) {
            return (
                <>
                    <Select
                        data={designations}
                        name="designation"
                        label="Designation"
                        required
                        searchable
                        placeholder="Select Designation"
                        className="col-span-2"
                        {...form.getInputProps('designation')}
                    />
                    <Select
                        data={roles}
                        name="userRoleId"
                        label="Role"
                        required
                        searchable
                        placeholder="Select Role"
                        value={storeManagerRoleId}
                        disabled
                        className="col-span-2"
                    />
                    <Select
                        data={stores}
                        name="managerStoreId"
                        label="Manages Store"
                        placeholder="Select Store"
                        searchable
                        className="col-span-2"
                        {...form.getInputProps('managerStoreId')}
                    />
                </>
            );
        }

        return (
            <>
                <div className="col-span-2">
                    {creatingNewDesignation ? (
                        <div className="flex flex-col justify-center gap-1">
                            <TextInput
                                name="designation"
                                label="Designation"
                                required
                                placeholder="Enter Designation"
                                autoFocus
                                {...form.getInputProps('designation')}
                            />
                            <Text
                                className="cursor-pointer text-primary ml-auto flex"
                                role="button"
                                onClick={() => setCreatingNewDesignation(false)}
                            >
                                Choose
                            </Text>
                        </div>
                    ) : (
                        <Select
                            data={designations}
                            name="designation"
                            label="Choose Designation"
                            required
                            searchable
                            placeholder="Select Designation"
                            {...form.getInputProps('designation')}
                            onChange={handleDesignationSelect}
                        />
                    )}
                </div>
                <Select
                    data={stores}
                    name="managerStoreId"
                    label="Manages Store"
                    placeholder="Select Store"
                    searchable
                    clearable
                    {...form.getInputProps('managerStoreId')}
                />
                <Select
                    data={sapUsers}
                    label="Sap User"
                    searchable
                    clearable
                    placeholder="Select Sap User"
                    {...form.getInputProps('sapUserId')}
                />
                <Select
                    data={roles}
                    name="userRoleId"
                    label="Role"
                    required
                    searchable
                    placeholder="Select Role"
                    {...form.getInputProps('userRoleId')}
                />
            </>
        );
    }, [isAdmin, form]);

    const onSubmit = async (values: any) => {
        try {
            const mutationVariables = {
                ...values,
                mobileNo: numberObjectMapper(
                    values.mobileNo?.number,
                    values.mobileNo?.code,
                ),
                telNo: values.telNo?.number
                    ? numberObjectMapper(
                          values.telNo?.number,
                          values.telNo?.code,
                      )
                    : null,
            };
            if (isEditing) {
                setUpdating(true);
                await updateUser(mutationVariables);
                onClose();
            } else {
                try {
                    showLoadingNotification(
                        'create-user',
                        'Creating user',
                        'Creating new user account',
                    );
                    const { id, ...input } = mutationVariables;
                    await createUser({
                        variables: {
                            input: { ...input, isActive: true },
                        },
                    });
                    updateSuccessNotification(
                        'create-user',
                        'User created',
                        'User account created successfully',
                    );
                    onClose();
                } catch (error: any) {
                    updateFailedNotification(
                        'create-user',
                        'Failed to create user',
                        error.message,
                    );
                }
            }
        } catch (error: any) {
            form.setFieldError('faxNo', error?.message);
        } finally {
            resetCreateUser();
            setUpdating(false);
        }
    };

    useEffect(() => {
        if (
            isEditing &&
            !designations.includes(initialValues?.designation ?? '')
        ) {
            setCreatingNewDesignation(true);
            form.setFieldValue('designation', initialValues?.designation ?? '');
        }
    }, [initialValues, designations, isEditing]);

    useEffect(() => {
        if (rolesError) {
            showErrorNotification('Error fetching roles');
        }
        if (storesError) {
            showErrorNotification('Error fetching stores');
        }
        if (sapUsersError) {
            showErrorNotification('Error fetching sap users');
        }
    }, [rolesError, storesError]);

    useDidUpdate(() => {
        if (form.values.firstName && form.values.lastName) {
            const existingUsernames =
                usersData?.getUsers?.map((user: User) => user.username) || [];
            const { username, warning } = generateUsername(
                form.values.firstName,
                form.values.lastName,
                existingUsernames,
            );
            form.setFieldValue('username', username);
            setUsernameWarning(warning);
        }
    }, [form.values.firstName, form.values.lastName]);

    return (
        <form
            onSubmit={form.onSubmit(onSubmit)}
            className="grid grid-cols-2 gap-6 w-full gap-y-8 p-4"
        >
            <TextInput
                name="firstName"
                label="First Name"
                placeholder="John"
                required
                className="w-full"
                {...form.getInputProps('firstName')}
            />

            <TextInput
                name="lastName"
                label="Last Name"
                placeholder="Doe"
                required
                className="w-full"
                {...form.getInputProps('lastName')}
            />

            <TextInput
                name="username"
                label="Username"
                placeholder="jdoe"
                disabled={isEditing || !isAdmin}
                className="w-full"
                {...form.getInputProps('username')}
            />

            {isAdmin && (
                <div className="col-span-2 flex flex-col gap-8">
                    {usernameWarning && (
                        <Alert
                            icon={<IconAlertCircle size="1rem" />}
                            title="Username Warning"
                            color="yellow"
                            className="mb-2"
                        >
                            {usernameWarning}
                        </Alert>
                    )}
                    <TextInput
                        name="email"
                        label="Email"
                        type="email"
                        placeholder="johndoe@mail.com"
                        {...form.getInputProps('email')}
                    />
                </div>
            )}
            {renderSelectFields()}
            <div className="col-span-2 grid grid-cols-6 gap-4">
                <NativeSelect
                    data={callingCodes}
                    label="Mobile Number"
                    required
                    {...form.getInputProps('mobileNo.code')}
                    className="col-span-2"
                    value={form.values.mobileNo?.code}
                />
                <TextInput
                    placeholder="4567890"
                    required
                    type="number"
                    className="col-span-4 mt-6"
                    {...form.getInputProps('mobileNo.number')}
                />
            </div>
            {isAdmin && (
                <>
                    <div className="col-span-2 grid grid-cols-6 gap-4">
                        <NativeSelect
                            data={callingCodes}
                            label="Telephone Number"
                            {...form.getInputProps('telNo.code')}
                            value={form.values.telNo?.code || ''}
                            className="col-span-2"
                        />
                        <TextInput
                            placeholder="4567890"
                            type="number"
                            className="col-span-4 mt-6"
                            {...form.getInputProps('telNo.number')}
                            error={form.errors.telNo}
                        />
                    </div>
                    <div className="col-span-2 grid grid-cols-4 gap-4">
                        <TextInput
                            name="faxNo"
                            label="Fax Number"
                            placeholder="(123)-456-7890"
                            className="col-span-2"
                            {...form.getInputProps('faxNo')}
                        />
                        <TextInput
                            name="speedDial"
                            label="Speed Dial"
                            placeholder="4231"
                            className="col-span-2"
                            {...form.getInputProps('speedDial')}
                        />
                    </div>
                    {!isEditing && isAdmin && (
                        <Switch
                            checked={form.values.sendInvite}
                            label="Send Invite"
                            size="md"
                            {...form.getInputProps('sendInvite')}
                        />
                    )}
                </>
            )}
            {Object.values(form.errors).length > 0 && (
                <Alert
                    color="red"
                    className="col-span-2"
                    icon={<IconAlertCircle size="1rem" />}
                    title="Please fill in all fields!"
                >
                    {Object.values(form.errors).map(error => (
                        <p>{error}</p>
                    ))}
                </Alert>
            )}
            <div className="col-span-2">
                <Button
                    size="md"
                    leftSection={
                        isEditing ? <IconUserEdit /> : <IconUserPlus />
                    }
                    type="submit"
                    loading={updating}
                >
                    {isEditing ? 'Update User' : 'Add User'}
                </Button>
            </div>
        </form>
    );
}

export default UserForm;
